import { config } from '@brands/config/config';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { LoginResponse } from '@brands/services/auth/login';
import { verifyMfaCode } from '@brands/services/auth/verifyMfaCode';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { formatEmail, formatPhoneNumber } from '@brands/Utils/formatDate';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import styles from './styles.module.scss';

interface MultiFactorAuthenticationProps {
  loginResponse: LoginResponse;
  setShowTwoFactorAuth: React.Dispatch<
    React.SetStateAction<{
      trigger: boolean;
      response: LoginResponse | null;
    }>
  >;
  userCredentials: {
    email: string;
    password: string;
  };
  triggerLogin: (response: LoginResponse) => Promise<void>;
  endLogin: (data: any, error: any) => void;
}

type UserSubmitForm = {
  code: string;
};

const MultiFactorAuthentication: FC<MultiFactorAuthenticationProps> = ({
  loginResponse,
  setShowTwoFactorAuth,
  userCredentials,
  triggerLogin,
  endLogin,
}) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const client = searchParams.get('client');
  const clientAccessCode = searchParams.get('client_access_code');

  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required('Verification Code is required')
      .matches(/^\d{6}$/, 'The code must be a 6-digit number.'),
  });

  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UserSubmitForm>({
    resolver: yupResolver(validationSchema),
  });

  const handleChange = (newValue: string): void => {
    setOtp(newValue);
    clearErrors('code');
  };
  const matchIsNumeric = (text: string): boolean => {
    return text.trim() !== '' && !isNaN(Number(text));
  };

  const validateChar = (value: string): boolean => {
    return matchIsNumeric(value);
  };

  const validateCode = async (newValue: string): Promise<void> => {
    setLoading(true);
    try {
      await verifyMfaCode({
        method: loginResponse.mfa_method as string,
        user_credentials: userCredentials,
        mfa_code: newValue,
        session: loginResponse.session_id as string,
        ...(client && { client }),
        ...(clientAccessCode && { client_access_code: clientAccessCode }),
        organization_id: config.organization_id,
        ...(config.division_id && { division_id: config.division_id }),
        ...(config.practice_id && { practice_id: config.practice_id }),
      })
        .then(async (res) => {
          await triggerLogin(res);
        })
        .catch((error) => {
          displayErrorDetails(error);
          setError('code', {
            type: 'manual',
            message:
              'The code you entered is invalid. Please enter the most recent verification code sent to your email.',
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      displayErrorDetails(error);
      setError('code', {
        type: 'manual',
        message: 'The code you entered is invalid. Please enter the most recent verification code sent to your email.',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (otp.length === 6) {
      validateCode(otp);
    }
  }, [otp]);

  const clickCancel = (): void => {
    setShowTwoFactorAuth({ trigger: false, response: null });
    endLogin(null, null);
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.dataTransfer.getData('text').trim();

    if (/^\d{6}$/.test(text)) {
      setOtp(text);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(text)) {
      e.preventDefault();
      setOtp(text);
    }
  };

  return (
    <>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog />
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <form className={styles.formContainer}>
            <span className={`${styles.formTitle} p-b-26 fs-unmask`}>
              <span className={styles.formTitleWelcome}>Let’s make sure it’s you</span>
            </span>
            {loginResponse.mfa_method === 'EMAIL_MFA' ? (
              <span className={`${styles.welcomeText} fs-unmask`}>
                {`We sent a verification code to ${formatEmail(loginResponse.email as string)}`}
              </span>
            ) : (
              <span className={`${styles.welcomeText} fs-unmask`}>
                {`We sent a verification code to your phone number ending in ${formatPhoneNumber(
                  loginResponse.phone_number as string,
                )}`}
              </span>
            )}
            <div className={styles.inputWrapper}>
              <MuiOtpInput
                className="fs-exclude"
                autoFocus
                value={otp}
                onChange={handleChange}
                length={6}
                TextFieldsProps={{
                  placeholder: '#',
                  disabled: loading,
                  inputMode: 'numeric',
                  type: 'tel',
                  onDrop: handleDrop,
                  onPaste: handlePaste,
                }}
                validateChar={validateChar}
              />

              <div className={`${styles.invalidFeedback} fs-unmask`}>{errors.code && errors.code.message}</div>
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.buttonWrapper}>
                <Button
                  type="button"
                  className={`${styles.formBtn} ${styles.cancel} fs-unmask`}
                  disabled={loading}
                  onClick={() => clickCancel()}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default MultiFactorAuthentication;
